/**
 * Deterministic floor plan for the Endless Palace (museum v2).
 *
 * Pure data, no three.js: semantic clusters go in, a chunked building comes
 * out. The plan is a *wandering* graph, not a straight enfilade: a grand
 * route of varied connector spaces (corridors, courts, crossings) grows from
 * the rotunda, turning as it goes; each topic cluster becomes a wing hanging
 * off the route, and the wing itself snakes room to room — straight runs,
 * left and right turns — through whatever space the plan hasn't claimed yet.
 * Every room is an instance of a room type (src/components/palace/roomTypes),
 * every save carries a bespoke frame sized from its own content, and walls
 * are hung as curated gallery-wall clusters rather than floor-to-ceiling
 * salon packing. Same archive in, same building out — the only randomness is
 * seeded PRNG streams.
 *
 * Rooms own their own four walls inside their own footprint, so adjacent
 * rooms simply abut; unrelated rooms keep a CLEARANCE air gap.
 */

import { textMetricsFor } from '../museumCanvas';
import { PALACE, SIDES, mulberry32, hashString, clamp } from './constants';
import { frameSizeFor } from './frameSize';
import { framedFootprint } from './frameStyles';
import { hangSegment } from './hang';
import { ROOM_TYPES, typesForRole } from './roomTypes';

export { PALACE };

const ROUTE_DOOR = { w: 3.4, h: 4.2 }; // grand arches along the route

// Chain clusters by topic-term overlap so semantically adjacent topics come
// out physically adjacent along the route. Greedy from the largest cluster;
// ties resolved by size then id, so the chain is deterministic.
const orderClusters = (clusters) => {
  const remaining = [...clusters].sort(
    (a, b) => b.size - a.size || String(a.id).localeCompare(String(b.id))
  );
  if (!remaining.length) return [];
  const chain = [remaining.shift()];
  while (remaining.length) {
    const tailTerms = new Set(chain[chain.length - 1].terms);
    let best = 0;
    let bestScore = -1;
    remaining.forEach((cluster, i) => {
      const score = cluster.terms.reduce((n, t) => n + (tailTerms.has(t) ? 1 : 0), 0);
      if (score > bestScore) {
        best = i;
        bestScore = score;
      }
    });
    chain.push(remaining.splice(best, 1)[0]);
  }
  return chain;
};

const tweetTime = (tweet) => {
  const t = Date.parse(tweet.created_at || tweet.metadata?.date || '');
  return Number.isFinite(t) ? t : 0;
};

// ---------------------------------------------------------------------------
// Placement machinery

// Weighted deterministic shuffle (exponential-jitter sort).
const weightedOrder = (types, rand) => types
  .map((t) => ({ t, k: -Math.log(1 - rand()) / (t.weight || 1) }))
  .sort((a, b) => a.k - b.k)
  .map((p) => p.t);

// Two footprints must keep an air gap unless one is the other's parent
// (those abut exactly at the shared doorway wall). `gapFor` lets a caller
// demand more air from specific neighbours than the default clearance.
const collides = (rect, nodes, ignore, gapFor) => nodes.some((r) => {
  if (r === ignore) return false;
  const C = gapFor ? gapFor(r) : PALACE.CLEARANCE;
  return !(
    rect.x1 >= r.x2 + C || rect.x2 <= r.x1 - C ||
    rect.z1 >= r.z2 + C || rect.z2 <= r.z1 - C
  );
});

const addDoor = (parent, child, side, at, w, h) => {
  const door = { side, at, w, h };
  parent.doors.push(door);
  child.doors.push({ side: SIDES[side].opp, at, w, h });
};

const makeNode = (id, type, role, rect, dims, palette) => ({
  id,
  type: type.id,
  role,
  ...rect,
  cx: (rect.x1 + rect.x2) / 2,
  cz: (rect.z1 + rect.z2) / 2,
  height: dims.height,
  palette,
  params: dims.params,
  doors: [],
  plaques: [],
  slots: [],
  aabb: { minX: rect.x1 - 1, maxX: rect.x2 + 1, minZ: rect.z1 - 1, maxZ: rect.z2 + 1 },
});

/**
 * Try to place one node of any listed type off `parent`, leaving through one
 * of `exitSides` (in order). Slides the child along the shared boundary when
 * the head-on position is taken. Returns { node, side } or null. On success
 * the connecting door is recorded on both nodes.
 */
const tryPlace = (typeList, parent, exitSides, rand, nodes, opts) => {
  const doorW = opts.doorW ?? PALACE.DOOR_W;
  const doorH = opts.doorH ?? PALACE.DOOR_H;
  const margin = doorW / 2 + 0.7; // door centre clearance from any corner

  for (const side of exitSides) {
    const axis = SIDES[side].dx !== 0 ? 'x' : 'z';
    const horizontal = side === 'N' || side === 'S'; // shared boundary runs along x
    const [p1, p2] = horizontal ? [parent.x1, parent.x2] : [parent.z1, parent.z2];
    if (p2 - p1 < margin * 2) continue;
    const at = clamp((p1 + p2) / 2, p1 + margin, p2 - margin);

    for (const type of weightedOrder(typeList, rand)) {
      for (let roll = 0; roll < 2; roll++) {
        const dims = type.plan(rand, { axis });
        const across = horizontal ? dims.w : dims.d; // child extent along the boundary
        const out = horizontal ? dims.d : dims.w; // child extent away from parent
        const maxSlide = Math.max(0, across / 2 - margin);
        for (const slide of [0, -maxSlide / 2, maxSlide / 2, -maxSlide, maxSlide]) {
          const c = at + slide; // child centre along the boundary
          const rect = horizontal
            ? {
              x1: c - across / 2,
              x2: c + across / 2,
              z1: side === 'N' ? parent.z2 : parent.z1 - out,
              z2: side === 'N' ? parent.z2 + out : parent.z1,
            }
            : {
              x1: side === 'E' ? parent.x2 : parent.x1 - out,
              x2: side === 'E' ? parent.x2 + out : parent.x1,
              z1: c - across / 2,
              z2: c + across / 2,
            };
          const c1 = horizontal ? rect.x1 : rect.z1;
          const c2 = horizontal ? rect.x2 : rect.z2;
          if (at < c1 + margin || at > c2 - margin) continue;
          if (collides(rect, nodes, parent, opts.gapFor)) continue;

          const node = makeNode(opts.id, type, opts.role, rect, dims, opts.palette);
          addDoor(parent, node, side, at, doorW, doorH);
          nodes.push(node);
          return { node, side };
        }
      }
    }
  }
  return null;
};

// Exit order for the next hop: straight-biased, turns shuffled.
const exitSidesFor = (travel, rand, straight = 0.62) => {
  const s = SIDES[travel];
  const turns = rand() < 0.5 ? [s.left, s.right] : [s.right, s.left];
  return rand() < straight ? [travel, ...turns] : [turns[0], travel, turns[1]];
};

// ---------------------------------------------------------------------------

/**
 * Build the whole plan.
 *
 * `clusters`: [{ id, label, terms, size, tweets }] — tweets already joined.
 * `annexTweets`: saves outside every cluster.
 *
 * Returns { chunks, metrics, spawns, tweetCount } where chunks are the
 * streamable node list, metrics is the global type-size basis for the
 * drawing layer, and spawns maps clusterId (and null → rotunda) to a camera
 * pose.
 *
 * A generator, because laying out ~9k frames takes long enough to freeze the
 * tab: the caller pumps it and hands the browser a frame between batches so
 * the entry screen can report where it has got to. `buildPalacePlan` below is
 * the synchronous wrapper for anything that just wants the plan.
 *
 * Yields `{ phase: 'route'|'grow'|'rescue'|'hang', done, total }` — `total` is
 * 0 where the work has no knowable end (wing growth stops when it stops).
 */
export function* planPalaceSteps(clusters, annexTweets) {
  const wings = orderClusters(
    clusters
      .filter((c) => c.tweets.length)
      .map((c) => ({
        ...c,
        tweets: [...c.tweets].sort((a, b) =>
          tweetTime(a) - tweetTime(b) || String(a.id).localeCompare(String(b.id))),
      }))
  );
  if (annexTweets.length) {
    wings.push({
      id: 'annex',
      label: 'Uncatalogued',
      terms: [],
      size: annexTweets.length,
      tweets: [...annexTweets].sort((a, b) =>
        tweetTime(a) - tweetTime(b) || String(a.id).localeCompare(String(b.id))),
    });
  }

  const nodes = [];
  const spawns = new Map();
  const allSlots = [];

  // The rotunda seeds the plan at the origin; the route leaves through its
  // north side.
  const rotundaType = ROOM_TYPES.rotunda;
  const rDims = rotundaType.plan(mulberry32(hashString('rotunda')), { axis: 'z' });
  const rotunda = makeNode(
    'rotunda',
    rotundaType,
    'entry',
    { x1: -rDims.w / 2, x2: rDims.w / 2, z1: -rDims.d / 2, z2: rDims.d / 2 },
    rDims,
    0
  );
  nodes.push(rotunda);
  spawns.set(null, {
    position: { x: 0, y: 1.7, z: -2 },
    target: { x: 0, y: 1.7, z: 8 },
  });

  const routeTypes = typesForRole('route');
  const wingTypes = typesForRole('wing');
  const routeRand = mulberry32(hashString('route'));

  // Offer this room `share` frames off the front of the wing's queue; whatever
  // its walls could not take goes back to the front for the next room.
  const hangNode = (node, queue, rand, share) => {
    const segs = ROOM_TYPES[node.type].segments(node) || [];
    const pool = queue.splice(0, Math.max(1, Math.round(share)));
    for (const seg of segs) node.slots.push(...hangSegment(seg, pool, rand));
    queue.unshift(...pool);
    allSlots.push(...node.slots);
  };

  // Roughly how many frames a room's walls will take under the organic hang,
  // as a share of its interior perimeter (calibrated headlessly against the
  // real archive). Growth is estimate-driven so all rooms — and all their
  // doors — exist before any wall is hung; the hang itself then divides the
  // queue by the same estimate, so a wing's saves spread evenly across its
  // rooms instead of packing the first few and leaving the rest bare.
  // Measured headlessly against the real archive: a gallery-wall cluster and an
  // eye-level row take about half a frame per metre of wall, while a hero wall
  // takes exactly one however long it is — estimating heroes by the metre is
  // what left the octagons and the door-flank stubs holding four pictures.
  const PER_METRE = { cluster: 0.5, row: 0.46 };
  const estimateCapacity = (node) => {
    const segs = ROOM_TYPES[node.type].segments(node) || [];
    if (!segs.length) return 0;
    const n = segs.reduce((sum, s) => sum
      + (s.style === 'hero' ? 1 : s.len * (PER_METRE[s.style] ?? PER_METRE.cluster)), 0);
    return Math.max(1, Math.round(n));
  };

  // A wing grows as a tree: it snakes forward room to room, and when the
  // newest room is walled in it branches from *any* earlier room in the wing.
  // All wings grow together, round-robin, one room per turn — grown one at a
  // time, an early wing sprawls across the open field and walls in the stops
  // of every wing after it. Only once growth is done — every door final — are
  // the walls hung, in placement order, so the chronological walk survives
  // and no frame ever sits where a later opening gets cut.
  const initWing = (wing, wingIndex, conn, sidePrefs, anchors = []) => {
    const rand = mulberry32(hashString(`wing:${wing.id}`));
    // The wing's own stretch of route hangs too, in walking order: the
    // corridors and courts a visitor crosses on the way in are the wing's
    // overture, not dead space between wings.
    const routeNodes = [...anchors, conn];
    routeNodes.forEach((node) => {
      node.clusterId = wing.id;
      node.label = wing.label;
    });
    return {
      wing,
      wingIndex,
      conn,
      sidePrefs,
      rand,
      queue: wing.tweets.map((tweet) => framedFootprint(tweet, frameSizeFor(tweet, rand))),
      rooms: [],
      // Branch points, newest preferred. The wing's whole stretch of route —
      // spacers as well as the stop — anchors growth: when the route folds
      // back and boxes the stop in, a spacer thirty metres up the corridor
      // usually still has an open flank.
      routeAnchors: new Set([conn, ...anchors]),
      candidates: [...anchors, conn],
      routeNodes,
      wingDir: sidePrefs[0], // outward from the route, set by the first room
      capacity: routeNodes.reduce((n, node) => n + estimateCapacity(node), 0),
      misses: 0,
      done: false,
    };
  };

  // How much estimated wall a wing grows past its queue before it stops. The
  // estimate is an average, and a wing that lands on the wrong side of it hangs
  // the shortfall nowhere: the slack is cheaper than a wing missing saves.
  const CAPACITY_SLACK = 1.35;

  // Try to add one room to the wing; marks the wing done when it has enough
  // estimated capacity for its queue or its miss budget runs out.
  const stepWing = (st) => {
    if (st.capacity >= st.queue.length * CAPACITY_SLACK || st.misses >= 250) {
      st.done = true;
      return;
    }
    // Growth leans away from the route so the wing becomes a deep arm rather
    // than a shell around its own connector.
    const outwardOrder = () => weightedOrder([
      { s: st.wingDir, weight: 3 },
      { s: SIDES[st.wingDir].left, weight: 1.2 },
      { s: SIDES[st.wingDir].right, weight: 1.2 },
      { s: SIDES[st.wingDir].opp, weight: 0.4 },
    ], st.rand).map((o) => o.s);
    let placed = null;
    let placedParent = null;
    for (let g = st.candidates.length - 1; g >= 0 && !placed; g--) {
      const parent = st.candidates[g];
      const sides = st.routeAnchors.has(parent) ? st.sidePrefs : outwardOrder();
      placed = tryPlace(wingTypes, parent, sides, st.rand, nodes, {
        id: `w${st.wingIndex}r${st.rooms.length}`,
        role: 'wing',
        palette: st.wingIndex,
      });
      if (placed) placedParent = parent;
      else st.misses++;
    }
    if (!placed) {
      st.done = true;
      return;
    }
    adoptRoom(st, placed, placedParent);
  };

  // Bookkeeping shared by normal growth and the rescue pass below.
  const adoptRoom = (st, placed, placedParent) => {
    const { node, side } = placed;
    node.clusterId = st.wing.id;
    node.label = st.wing.label;
    if (st.rooms.length === 0) {
      st.wingDir = side;
      placedParent.plaques.push({ side, at: node.doors[0].at, label: st.wing.label });
      spawns.set(String(st.wing.id), {
        position: { x: node.cx, y: 1.7, z: node.cz },
        target: {
          x: node.cx + SIDES[side].dx * 4,
          y: 1.7,
          z: node.cz + SIDES[side].dz * 4,
        },
      });
    }
    st.rooms.push(node);
    st.candidates.push(node);
    st.capacity += estimateCapacity(node);
  };

  // Hang in walking order — the wing's own route first, then its rooms as
  // they were placed — giving each space a share of what is left in
  // proportion to how much wall it has. Handing every room its share rather
  // than letting the front of the queue fill the first rooms to bursting is
  // what stops a wing ending in a run of empty ones; a room that cannot take
  // its whole share hands the remainder straight back, so the next room's
  // share simply grows.
  const finishWing = (st) => {
    const spaces = [...st.routeNodes, ...st.rooms];
    let wallLeft = spaces.reduce((n, node) => n + estimateCapacity(node), 0);
    for (const node of spaces) {
      const wall = estimateCapacity(node);
      if (!wall) continue;
      hangNode(node, st.queue, st.rand, wallLeft > 0
        ? (st.queue.length * wall) / wallLeft
        : st.queue.length);
      wallLeft -= wall;
    }
    if (st.queue.length) {
      console.warn(
        `palace: wing ${st.wing.id} left ${st.queue.length}/${st.wing.tweets.length} unplaced `
        + `(${st.rooms.length} rooms, est capacity ${st.capacity}, ${st.misses} misses)`
      );
    }
  };

  // The route is laid first — the palace's spine exists before any wing can
  // wall it in. It runs mostly straight with the occasional turn, and each
  // wing's stop is preceded by spacer segments sized to the wing, so a big
  // wing gets a long stretch of open flank to grow into. If the chain still
  // corners itself it branches from an earlier connector.
  const frontier = [rotunda];
  const routeStops = [];
  // A corridor may hug its chain neighbours (it turns a corner against them)
  // but must keep a wing-sized flank from every other stretch of route, so
  // the spine never folds back leaving pockets too small for any room.
  // (13m ≈ one gallery deep plus clearance; tuned with the headless harness —
  // below it wings starve, above it the route can no longer turn.)
  const ROUTE_GAP = 13;
  let travel = 'N';
  const placeRouteNode = (id, palette) => {
    let placed = null;
    // The gap is a preference, not a law: when the whole frontier fails at
    // full flank width the route squeezes through rather than dropping a wing.
    for (const gap of [ROUTE_GAP, 9.5, PALACE.CLEARANCE]) {
      for (let f = frontier.length - 1; f >= 0 && !placed; f--) {
        const from = frontier[f];
        const neighbours = new Set([frontier[f - 1], frontier[f + 1]]);
        const order = f === frontier.length - 1
          ? exitSidesFor(travel, routeRand, 0.8)
          : weightedOrder(['N', 'E', 'S', 'W'].map((s) => ({ s, weight: 1 })), routeRand)
            .map((o) => o.s);
        placed = tryPlace(routeTypes, from, order, routeRand, nodes, {
          id,
          role: 'route',
          palette,
          doorW: ROUTE_DOOR.w,
          doorH: ROUTE_DOOR.h,
          gapFor: (other) => (neighbours.has(other) ? PALACE.CLEARANCE : gap),
        });
      }
      if (placed) break;
    }
    if (placed) {
      travel = placed.side;
      frontier.push(placed.node);
    }
    return placed;
  };
  for (let i = 0; i < wings.length; i++) {
    const wing = wings[i];
    const spacers = clamp(Math.round(wing.tweets.length / 110), 1, 6);
    const anchors = [];
    for (let k = 0; k < spacers; k++) {
      const spacer = placeRouteNode(`c${i}s${k}`, i);
      if (spacer) anchors.push(spacer.node);
    }
    const placed = placeRouteNode(`c${i}`, i);
    if (!placed) console.warn(`palace: no room left to route wing ${wing.id}`);
    routeStops.push(placed && { ...placed, anchors });
    yield { phase: 'route', done: i + 1, total: wings.length };
  }

  // Wings grow into the space around the finished route, alternating sides,
  // all together one room at a time so no wing walls in another's stop.
  const wingStates = wings
    .map((wing, i) => {
      const stop = routeStops[i];
      if (!stop) return null;
      const s = SIDES[stop.side];
      const wingSides = i % 2 === 0
        ? [s.left, s.right, stop.side, s.opp]
        : [s.right, s.left, stop.side, s.opp];
      return initWing(wing, i, stop.node, wingSides, stop.anchors);
    })
    .filter(Boolean);
  let growing = true;
  while (growing) {
    growing = false;
    for (const st of wingStates) {
      if (st.done) continue;
      stepWing(st);
      if (!st.done) growing = true;
    }
    yield { phase: 'grow', done: nodes.length, total: 0 };
  }

  // Rescue pass: a wing the palace folded shut around continues as annex
  // rooms cut from the nearest open wall anywhere in the building — real
  // palaces sprout annexes too. The plaque stays at the wing's stop; only the
  // overflow wanders. Must run before any hanging so every door cut is final.
  for (let w = 0; w < wingStates.length; w++) {
    const st = wingStates[w];
    let rescueMisses = 0;
    while (st.capacity < st.queue.length * CAPACITY_SLACK && rescueMisses < 400) {
      const tail = st.rooms[st.rooms.length - 1] || st.conn;
      const hosts = [...nodes]
        .filter((n) => n.role !== 'entry')
        .sort((a, b) => ((a.cx - tail.cx) ** 2 + (a.cz - tail.cz) ** 2)
          - ((b.cx - tail.cx) ** 2 + (b.cz - tail.cz) ** 2));
      let placed = null;
      let placedParent = null;
      for (const host of hosts) {
        const sides = weightedOrder(
          ['N', 'E', 'S', 'W'].map((s) => ({ s, weight: 1 })), st.rand
        ).map((o) => o.s);
        placed = tryPlace(wingTypes, host, sides, st.rand, nodes, {
          id: `w${st.wingIndex}r${st.rooms.length}`,
          role: 'wing',
          palette: st.wingIndex,
        });
        if (placed) { placedParent = host; break; }
        if (++rescueMisses >= 400) break;
      }
      if (!placed) break;
      adoptRoom(st, placed, placedParent);
    }
    yield { phase: 'rescue', done: w + 1, total: wingStates.length };
  }

  for (let w = 0; w < wingStates.length; w++) {
    finishWing(wingStates[w]);
    yield { phase: 'hang', done: allSlots.length, total: 0 };
  }

  return {
    chunks: nodes,
    metrics: textMetricsFor(allSlots.map((s) => ({ height: s.h }))),
    spawns,
    tweetCount: allSlots.length,
  };
}

// Synchronous plan build — pumps the generator to the end and returns the plan.
export const buildPalacePlan = (clusters, annexTweets) => {
  const steps = planPalaceSteps(clusters, annexTweets);
  let step = steps.next();
  while (!step.done) step = steps.next();
  return step.value;
};
